import { themeAtom } from "../../shared/atoms";
import { RouteParams } from "../models/VoyagePnl.model";
import { continueClickedAtomFamily, enabledFlagsAtomFamily, PnlContext, revenueFields } from "../utils/UtilFunctions";
import PnlTableRow from "./PnlTableRow";
import { useAtomValue } from "jotai";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

const PnlTable: React.FC = () => {
  const theme = useAtomValue(themeAtom);
  const { tcCode, voyageNo } = useParams<RouteParams>();
  const voyageScope = useMemo(() => ({ tcCode, voyageNo }), [tcCode, voyageNo]);

  const voyagePnlData = useAtomValue(PnlContext);
  const continueClicked = useAtomValue(continueClickedAtomFamily(voyageScope));
  const enabledFlags = useAtomValue(enabledFlagsAtomFamily(voyageScope));
  const revenueKeyDrivers = voyagePnlData?.keyDrivers?.filter((item) => revenueFields.includes(item.category));
  const expenseKeyDrivers = voyagePnlData?.keyDrivers?.filter((item) => !revenueFields.includes(item.category));

  if (!voyagePnlData) return null; // or a loading state, fallback UI, etc.

  return (
    <table
      className={twMerge(
        "w-full max-w-full",
        (continueClicked && enabledFlags > 3) || (!continueClicked && enabledFlags > 2) ? "border-b" : "",
        "text-right text-[12px]",
        theme.uwhcAssistant.text,
        theme.uwhcAssistant.border,
      )}
    >
      <thead>
        <tr className={twMerge("h-[44px] border-b", theme.uwhcAssistant.border)}>
          <th className="h-full px-4 text-left font-bold">KEY DRIVERS</th>

          <th colSpan={continueClicked ? 2 : 4} className="h-full px-4" />
        </tr>
        <tr className={twMerge("h-[44px] border-b", theme.uwhcAssistant.border)}>
          <th colSpan={continueClicked ? 2 : 4} className="h-full px-4" />
        </tr>
        <tr className={twMerge("h-[44px] border-b", theme.home.tailwind.backgound, theme.uwhcAssistant.border)}>
          <th className="h-full min-w-[180px] max-w-[180px] px-4 text-left">CATEGORY</th>
          {!continueClicked && (
            <>
              <th className="h-full w-[22%] px-4">ESTIMATE</th>
              <th className="h-full w-[22%] px-4">ACTUAL</th>
            </>
          )}
          <th className={`h-full px-4 ${continueClicked ? "w-[44%]" : "w-[22%]"}`}>VARIANCE</th>
          <th className={`h-full px-4 ${continueClicked ? "w-[44%]" : "w-[22%]"}`}>UNALLOCATED</th>
        </tr>
      </thead>
      <tbody className="">
        <tr className={twMerge("h-[44px] border-b bg-opacity-60", theme.voyPnl.rowGreenBg, theme.uwhcAssistant.border)}>
          <th className="h-full px-4 text-left font-bold">REVENUES</th>

          <th colSpan={continueClicked ? 2 : 4} className="h-full px-4" />
        </tr>
        {revenueKeyDrivers?.map((row, index) => (
          <PnlTableRow
            key={index}
            category={row.category}
            estimate={row.estimate}
            actual={row.actual}
            variance={row.variance}
            unallocated={row.unallocated}
            continueClicked={continueClicked}
          />
        ))}
        <tr className={twMerge("h-[44px] border-y bg-opacity-60", theme.voyPnl.rowRedBg, theme.uwhcAssistant.border)}>
          <th className="h-full px-4 text-left font-bold">EXPENSES</th>

          <th colSpan={continueClicked ? 2 : 4} className="h-full px-4" />
        </tr>
        {expenseKeyDrivers?.map((row, index) => (
          <PnlTableRow
            key={index}
            category={row.category}
            estimate={row.estimate}
            actual={row.actual}
            variance={row.variance}
            unallocated={row.unallocated}
            continueClicked={continueClicked}
          />
        ))}
      </tbody>
    </table>
  );
};

export default PnlTable;
